// The child-wise plan table of Create Schedule (/daily-schedule/create, view, edit): its two header
// rows and its cells. The shared visit page (/daily-schedule/shared) and the coach Daily Lesson Plan
// show their plans in the same table.
import React from "react";
import { BarChart3, BookOpen, Laptop, Lightbulb, Monitor, Music, Target } from "lucide-react";
import { COMPUTER_APPS } from "../constants/lessonPlanCatalog";
import { COMPUTER_APP_NAMES, LEARNING_COLUMNS, buildColumnOptions, learningFromSaved } from "./lessonPlanColumns";

// Header and cell icons of the One-on-One columns (scene cells have none, as in the design).
export const COLUMN_ICONS = {
  beginnerScene: { Icon: BookOpen, color: "#f97316" },
  intermediateScene: { Icon: BarChart3, color: "#2563eb" },
  actionSong: { Icon: Music, color: "#e11d48" },
  story: { Icon: BookOpen, color: "#1d4ed8" },
  lifeSkill: { Icon: Lightbulb, color: "#eab308" },
};
export const cellIcon = (key, value) => {
  if (!value || key === "beginnerScene" || key === "intermediateScene") return null;
  if (key === "actionSong") return { Icon: Music, color: /^vocal/i.test(value) ? "#db2777" : "#16a34a" };
  return COLUMN_ICONS[key];
};
export const COLUMN_ICON_OF = Object.fromEntries(LEARNING_COLUMNS.map(({ key }) => [key, (value) => cellIcon(key, value)]));

// What saved plans are read against without the course content: no options (every item comes from
// the courses), so a value goes back by its saved column, or as an older plan's sample item.
export const STATIC_OPTIONS = buildColumnOptions();
// A plan row's columns; rows saved before the columns existed (or drafts of them) have one `oneOnOne`.
export const rowLearning = (row) => row?.learning || learningFromSaved(row, STATIC_OPTIONS);

// Computer Apps cells offer the apps; every app has an icon.
export const COMPUTER_GROUPS = [{ label: "Computer Apps", options: COMPUTER_APP_NAMES }];
// Unknown apps get a plain monitor rather than another app's icon.
export const fixedAppMeta = (name) => COMPUTER_APPS.find((a) => a.name === name) || { Icon: Monitor, color: "#0b2fb0" };

export function FixedValue({ value, meta }) {
  if (!value) return <span className="lp-fixed-empty">—</span>;
  const { Icon, color } = meta(value);
  return (
    <div className="lp-select lp-select--fixed">
      <Icon size={17} color={color} aria-hidden="true" />
      <span>{value}</span>
    </div>
  );
}

export function FixedLearning({ column, value }) {
  if (!value) return <span className="lp-fixed-empty">—</span>;
  const icon = cellIcon(column, value);
  return (
    <div className="lp-select lp-select--fixed lp-select--cell">
      {icon && <icon.Icon size={16} color={icon.color} aria-hidden="true" />}
      <span>{value}</span>
    </div>
  );
}

// "#, Child Name, Computer Apps, One-on-One — Personalized Learning (one column each), Special Instructions".
export function PlanTableHead() {
  return (
    <thead>
      <tr>
        <th rowSpan={2}>#</th>
        <th rowSpan={2}>Child Name</th>
        <th rowSpan={2}><Laptop size={18} color="#1d4ed8" />Computer Apps</th>
        <th colSpan={LEARNING_COLUMNS.length} className="lp-table__group">One-on-One — Personalized Learning</th>
        <th rowSpan={2}><Target size={18} color="#1d4ed8" />Special Instructions</th>
      </tr>
      <tr>
        {LEARNING_COLUMNS.map(({ key, label }) => {
          const { Icon, color } = COLUMN_ICONS[key];
          return <th key={key} className="lp-table__sub"><Icon size={15} color={color} />{label}</th>;
        })}
      </tr>
    </thead>
  );
}
